import { Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Camera, FileText, Image } from "lucide-react";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import { useDocumentUploadMutation } from "web_myplann/Api/Document/useDocumentUploadMutation";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { base64ToFile } from "web_myplann/Infrastructure/Utils/FileUtils";
import { device } from "web_myplann/Utils/device";

type Props = {
	onClose: () => void;
};

export const AddDocumentDrawer: React.FunctionComponent<Props> = ({
	onClose,
}) => {
	const theme = useTheme();
	const { t } = useResource();

	const { mutate: uploadDocument } = useDocumentUploadMutation();
	const queryClient = useQueryClient();

	const handleUploadDocument = (file: File, filename: string) => {
		uploadDocument(
			{
				data: {
					title: filename,
					file,
				},
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: DocumentKeys.allLists,
					});
					onClose();
				},
			},
		);
	};

	const handlePickAttachmentPhoto = async () => {
		const response = await device.pickAttachmentPhoto();
		const file = base64ToFile(
			response.payload.file.base64!,
			response.payload.file.filename!,
		);
		handleUploadDocument(file, response.payload.file.filename);
	};

	const handlePickAttachmentGallery = async () => {
		const response = await device.pickAttachmentGallery();
		const file = base64ToFile(
			response.payload.file.base64!,
			response.payload.file.filename!,
		);
		handleUploadDocument(file, response.payload.file.filename);
	};

	const handlePickAttachmentFile = async () => {
		const response = await device.pickAttachmentFile();
		const file = base64ToFile(
			response.payload.file.base64!,
			response.payload.file.filename!,
		);
		handleUploadDocument(file, response.payload.file.filename);
	};

	return (
		<Stack px={1.6} gap={1.6} pb={1.6}>
			<Stack
				direction="row"
				alignItems="center"
				gap={1.6}
				onClick={handlePickAttachmentPhoto}
			>
				<Camera size={theme.iconSize.md} color={theme.palette.primary.main} />
				<Typography>
					{t(Resources.Portfolio.List.AddDocumentDrawer.Types.photo)}
				</Typography>
			</Stack>
			<Stack
				direction="row"
				alignItems="center"
				gap={1.6}
				onClick={handlePickAttachmentGallery}
			>
				<Image size={theme.iconSize.md} color={theme.palette.primary.main} />
				<Typography>
					{t(Resources.Portfolio.List.AddDocumentDrawer.Types.gallery)}
				</Typography>
			</Stack>
			<Stack
				direction="row"
				alignItems="center"
				gap={1.6}
				onClick={handlePickAttachmentFile}
			>
				<FileText size={theme.iconSize.md} color={theme.palette.primary.main} />
				<Typography>
					{t(Resources.Portfolio.List.AddDocumentDrawer.Types.file)}
				</Typography>
			</Stack>
		</Stack>
	);
};
